/**
 * PlaceholderRotator Hook
 *
 * Cycles through fun placeholder suggestions with a typing animation
 * Follows LiveView best practices - uses querySelector scoped to this.el
 */

const PlaceholderRotator = {
  mounted() {
    this.initializePlaceholders();
    this.setupTextarea();
    this.animatePlaceholder();
  },

  updated() {
    // Don't reinitialize on updates - preserve animation state
    // Just make sure textarea reference is still valid
    if (!this.textarea || !document.contains(this.textarea)) {
      this.setupTextarea();
    }
  },

  destroyed() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    if (this.textarea) {
      if (this.isContentEditable) {
        this.textarea.dataset.placeholder = this.originalPlaceholder || this.textarea.dataset.placeholder;
      } else {
        this.textarea.placeholder = this.originalPlaceholder || this.textarea.placeholder;
      }
      if (this.focusHandler) {
        this.textarea.removeEventListener('focus', this.focusHandler);
      }
      if (this.blurHandler) {
        this.textarea.removeEventListener('blur', this.blurHandler);
      }
    }
  },

  initializePlaceholders() {
    const placeholders = [
      "Explain what's on this page",
      'Help me write an email',
      'Type "/" to bring up skills',
      'Use @tab to reference tabs'
    ];

    // Randomize the order using Fisher-Yates shuffle
    this.placeholders = placeholders.slice();
    for (let i = this.placeholders.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.placeholders[i], this.placeholders[j]] = [this.placeholders[j], this.placeholders[i]];
    }

    this.currentIndex = 0;
    this.charIndex = 0;
    this.isTyping = true;
    this.isDeleting = false;
  },

  setupTextarea() {
    // Get the target element (textarea or contenteditable)
    const targetId = this.el.dataset.target;
    this.textarea = document.getElementById(targetId);

    if (!this.textarea) {
      return;
    }

    // Check if it's contenteditable
    this.isContentEditable = this.textarea.hasAttribute('contenteditable');

    // Store the original placeholder
    this.originalPlaceholder = this.isContentEditable
      ? this.textarea.dataset.placeholder
      : this.textarea.placeholder;

    // Create bound methods once if they don't exist
    if (!this.focusHandler) {
      this.focusHandler = this.handleFocus.bind(this);
    }
    if (!this.blurHandler) {
      this.blurHandler = this.handleBlur.bind(this);
    }

    // Remove old event listeners before adding new ones
    this.textarea.removeEventListener('focus', this.focusHandler);
    this.textarea.removeEventListener('blur', this.blurHandler);

    // Add event listeners
    this.textarea.addEventListener('focus', this.focusHandler);
    this.textarea.addEventListener('blur', this.blurHandler);
  },

  handleFocus() {
    if (this.textarea) {
      if (this.isContentEditable) {
        this.textarea.dataset.placeholder = '';
      } else {
        this.textarea.placeholder = '';
      }
    }
  },

  handleBlur() {
    if (this.textarea) {
      const isEmpty = this.isContentEditable
        ? this.textarea.textContent.trim().length === 0
        : this.textarea.value.length === 0;

      if (isEmpty) {
        this.charIndex = 0;
        this.isDeleting = false;
      }
    }
  },

  animatePlaceholder() {
    if (!this.textarea) return;

    const currentPlaceholder = this.placeholders[this.currentIndex];

    // Check if element has content
    const hasContent = this.isContentEditable
      ? this.textarea.textContent.trim().length > 0
      : this.textarea.value.length > 0;

    // Only animate if the element is empty and not focused (when window is focused)
    // Continue animation when window loses focus, even if element would have focus
    if (hasContent ||
        (document.activeElement === this.textarea && document.hasFocus())) {
      this.timeout = setTimeout(() => this.animatePlaceholder(), 100);
      return;
    }

    // Set placeholder text
    const setPlaceholder = (text) => {
      if (this.isContentEditable) {
        this.textarea.dataset.placeholder = text;
      } else {
        this.textarea.placeholder = text;
      }
    };

    if (this.isDeleting) {
      // Delete character
      this.charIndex--;
      setPlaceholder(currentPlaceholder.substring(0, this.charIndex));

      if (this.charIndex === 0) {
        this.isDeleting = false;
        this.currentIndex = (this.currentIndex + 1) % this.placeholders.length;
        this.timeout = setTimeout(() => this.animatePlaceholder(), 500);
        return;
      }

      this.timeout = setTimeout(() => this.animatePlaceholder(), 30);
    } else {
      // Type character
      this.charIndex++;
      setPlaceholder(currentPlaceholder.substring(0, this.charIndex));

      if (this.charIndex === currentPlaceholder.length) {
        // Pause at the end before deleting
        this.timeout = setTimeout(() => {
          this.isDeleting = true;
          this.animatePlaceholder();
        }, 3000);
        return;
      }

      // Vary typing speed slightly for more natural effect
      const typingSpeed = 50 + Math.random() * 50;
      this.timeout = setTimeout(() => this.animatePlaceholder(), typingSpeed);
    }
  }
};

export default PlaceholderRotator;
